import React, { useEffect, useMemo, useRef, useState } from 'react'
import html2canvas from 'html2canvas'
import { jsPDF } from 'jspdf'
import { db } from '../firebase'
import { getAuth, onAuthStateChanged } from "firebase/auth"
import { collection, query, getDocs, where } from "firebase/firestore"
import defaultAvatar from '../Images/defprofile.png'
import logo from '../Images/logo-main-black.png'
import shapeOne from '../Images/Shape 1.svg'
import shapeTwo from '../Images/Shape 2.svg'

const SCHOOL_ID_TERMS = [
  "This card is the property of Ubunifu College and must be returned upon withdrawal or graduation.",
  "It must be carried at all times while on campus.",
  "Lost cards must be reported to the Registrar immediately.",
]

const PRINT_ID_WIDTH_INCHES = 2.125
const PRINT_ID_HEIGHT_INCHES = 3.375
const PDF_POINTS_PER_INCH = 72
const PRINT_ID_WIDTH_PT = PRINT_ID_WIDTH_INCHES * PDF_POINTS_PER_INCH
const PRINT_ID_HEIGHT_PT = PRINT_ID_HEIGHT_INCHES * PDF_POINTS_PER_INCH

const isEligibleForSchoolId = (value) => {
  const normalized = (value || "").toString().trim().toLowerCase()
  return normalized === "student" || normalized === "completed"
}

const sanitizeFileName = (value) => {
  const safeName = (value || "student")
    .toString()
    .trim()
    .replace(/\s+/g, "_")
    .replace(/[^a-zA-Z0-9_-]/g, "")
  return safeName || "student"
}

function StudentIdComp() {
  const [studentRecord, setStudentRecord] = useState({
    name: "",
    course: "",
    cohort: "",
    verificationId: "",
    photo: defaultAvatar,
    status: "",
  })
  const [isLoading, setIsLoading] = useState(true)
  const [isDownloading, setIsDownloading] = useState(false)
  const [cardOrder, setCardOrder] = useState("front-back")

  const frontCardRef = useRef(null)
  const backCardRef = useRef(null)
  const auth = getAuth()

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (!user) {
        setIsLoading(false)
        return
      }

      setIsLoading(true)
      try {
        const q = query(collection(db, "users"), where("userId", "==", user.uid))
        const snapshot = await getDocs(q)
        const userData = snapshot.docs[0]?.data() || {}
        const verificationId = userData.admissionNumber
          ? String(userData.admissionNumber).trim()
          : (userData.userId || user.uid || "")

        setStudentRecord({
          name: userData.bio || userData.userName || user.displayName || "Student",
          course: userData.course || "",
          cohort: userData.cohort || "",
          verificationId,
          photo: userData.profilePic && String(userData.profilePic) !== "undefined"
            ? userData.profilePic
            : defaultAvatar,
          status: userData.userStatus || userData.Status || "",
        })
      } catch (error) {
        console.error("Error loading school ID data:", error)
        setStudentRecord((current) => ({
          ...current,
          name: user.displayName || "Student",
          verificationId: user.uid || "",
        }))
      } finally {
        setIsLoading(false)
      }
    })

    return () => unsubscribe()
  }, [auth])

  const canViewSchoolId = useMemo(() => {
    return isEligibleForSchoolId(studentRecord.status)
  }, [studentRecord.status])

  const verificationUrl = useMemo(() => {
    if (!studentRecord.verificationId) return ""
    return `https://verify.ubunifucollege.com/school-id/${encodeURIComponent(studentRecord.verificationId)}`
  }, [studentRecord.verificationId])

  const qrCodeUrl = useMemo(() => {
    if (!verificationUrl) return ""
    return `https://api.qrserver.com/v1/create-qr-code/?size=240x240&margin=0&data=${encodeURIComponent(verificationUrl)}`
  }, [verificationUrl])

  const exportSideToPdfPage = async (pdf, ref, shouldAddPage) => {
    if (!ref.current) return
    const canvas = await html2canvas(ref.current, {
      scale: 4,
      useCORS: true,
      backgroundColor: "#ffffff",
    })
    const imageData = canvas.toDataURL("image/png")

    if (shouldAddPage) {
      pdf.addPage([PRINT_ID_WIDTH_PT, PRINT_ID_HEIGHT_PT], "portrait")
    }

    pdf.addImage(
      imageData,
      "PNG",
      0,
      0,
      PRINT_ID_WIDTH_PT,
      PRINT_ID_HEIGHT_PT,
      undefined,
      "FAST"
    )
  }

  const downloadSchoolId = async () => {
    if (!frontCardRef.current || !backCardRef.current) return
    setIsDownloading(true)
    try {
      const pdf = new jsPDF({
        orientation: "portrait",
        unit: "pt",
        format: [PRINT_ID_WIDTH_PT, PRINT_ID_HEIGHT_PT],
        compress: true,
      })
      await exportSideToPdfPage(pdf, frontCardRef, false)
      await exportSideToPdfPage(pdf, backCardRef, true)
      pdf.save(`School_ID_${sanitizeFileName(studentRecord.name)}.pdf`)
    } catch (error) {
      console.error("Error exporting school ID:", error)
    } finally {
      setIsDownloading(false)
    }
  }

  const frontCard = (
    <div ref={frontCardRef} className="schoolIdReplicaCard schoolIdReplicaCard--front">
      <div className="schoolIdReplicaNotch" />
      <img src={shapeOne} alt="" className="schoolIdReplicaTopArt" />
      <img src={shapeTwo} alt="" className="schoolIdReplicaBottomArt" />

      <div className="schoolIdReplicaInner schoolIdReplicaInner--front">
        <img src={logo} alt="Ubunifu College" className="schoolIdReplicaLogo" />

        <div className="schoolIdReplicaPhotoWrap">
          <img
            src={studentRecord.photo || defaultAvatar}
            alt={studentRecord.name || "Student"}
            className="schoolIdReplicaPhoto"
          />
        </div>

        <h3 className="schoolIdReplicaName">{studentRecord.name || "Student"}</h3>
        <div className="schoolIdReplicaRole">STUDENT</div>
        <p className="schoolIdReplicaIdNumber">Student ID No: {studentRecord.verificationId || "--"}</p>
      </div>
    </div>
  )

  const backCard = (
    <div ref={backCardRef} className="schoolIdReplicaCard schoolIdReplicaCard--back">
      <div className="schoolIdReplicaNotch" />
      <img src={shapeOne} alt="" className="schoolIdReplicaTopArt" />
      <img src={shapeTwo} alt="" className="schoolIdReplicaBottomArt" />

      <div className="schoolIdReplicaInner schoolIdReplicaInner--back">
        <h3 className="schoolIdReplicaBackTitle">Ubunifu College</h3>
        <p className='expirydateOnId'>Expiry Date: 24/07/2028</p>

        <div className="schoolIdReplicaQrWrap">
          {qrCodeUrl ? (
            <img src={qrCodeUrl} alt="School ID verification QR code" className="schoolIdReplicaQr" />
          ) : (
            <div className="schoolIdReplicaQrFallback">QR unavailable</div>
          )}
        </div>

        <ul className="schoolIdReplicaTerms">
          {SCHOOL_ID_TERMS.map((term) => (
            <li key={term}>{term}</li>
          ))}
        </ul>
      </div>
    </div>
  )

  const orderedCards = cardOrder === "back-front"
    ? [backCard, frontCard]
    : [frontCard, backCard]

  return (
    <div className="schoolIdPage">
      <div className="schoolIdPageHeader">
        <div>
          <h5>School ID</h5>
          <p>Toggle the order: front &amp; back or back &amp; front.</p>
        </div>
      </div>

      {isLoading ? (
        <div className="schoolIdStateCard">
          <p>Checking admission status...</p>
        </div>
      ) : !canViewSchoolId ? (
        <div className="schoolIdStateCard schoolIdStateCard--unavailable">
          <h6>School ID Unavailable</h6>
          <p>School ID is available only for admitted students.</p>
        </div>
      ) : (
        <>
          <div className="schoolIdToolbar">
            <div className="schoolIdToggleGroup">
              <button
                type="button"
                className={cardOrder === "front-back" ? "schoolIdToggleBtn active" : "schoolIdToggleBtn"}
                onClick={() => setCardOrder("front-back")}
              >
                Show Front &amp; Back
              </button>
              <button
                type="button"
                className={cardOrder === "back-front" ? "schoolIdToggleBtn active" : "schoolIdToggleBtn"}
                onClick={() => setCardOrder("back-front")}
              >
                Show Back &amp; Front
              </button>
            </div>

            <button
              type="button"
              className="schoolIdDownloadBtn"
              onClick={downloadSchoolId}
              disabled={isDownloading}
            >
              {isDownloading ? "Preparing..." : "Download ID"}
            </button>
          </div>

          <div className="schoolIdDeck">
            {orderedCards.map((card, index) => (
              <div className="schoolIdDeckItem" key={`school-id-card-${index}`}>
                {card}
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  )
}

export default StudentIdComp
